/**
 * SPAACIO Product Hydrator
 * ------------------------
 * Módulo que obtiene datos frescos de productos desde el endpoint
 * nativo de Shopify: /products/{handle}.js
 *
 * Resuelve el problema de precios obsoletos durante sales (Hot Sale, etc).
 * Los handles persisten en localStorage, pero precios/stock/imagen se
 * consultan en tiempo real cada vez que se renderiza.
 *
 * v1.1.0 - Fix: productos 404 ahora se distinguen de errores de red.
 *   - 404 → producto eliminado, se filtra Y se elimina del localStorage
 *   - Error de red → mantiene cache como fallback (mejor mostrar viejo que nada)
 *
 * @version 1.1.0
 */

(function () {
  'use strict';

  // Estados especiales devueltos por hydrateProduct para distinguir tipos de error
  const FETCH_404 = Symbol('FETCH_404');     // Producto no existe en Shopify (eliminado)
  const FETCH_ERROR = Symbol('FETCH_ERROR'); // Error de red u otro tipo (mantener cache)

  /**
   * Construye la URL del endpoint nativo de Shopify para un producto.
   * Respeta el prefijo de localización si existe (ej: /es-mx/products/...).
   */
  function buildProductUrl(handle) {
    if (!handle) return '';
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js';
  }

  /**
   * Normaliza la respuesta de Shopify al formato esperado por el tracker.
   */
  function normalizeProduct(data) {
    if (!data || !data.handle) return null;

    let image = '';
    if (data.featured_image) {
      image = data.featured_image;
    } else if (data.images && data.images.length > 0) {
      image = data.images[0];
    }

    if (image && image.indexOf('//') === 0) {
      image = 'https:' + image;
    }

    let price = data.price || 0;
    let comparePrice = data.compare_at_price || 0;
    let available = data.available !== false;

    if (data.variants && data.variants.length > 0) {
      const availableVariants = data.variants.filter(v => v.available);
      if (availableVariants.length > 0) {
        const defaultVariant = availableVariants[0];
        price = defaultVariant.price || price;
        comparePrice = defaultVariant.compare_at_price || comparePrice;
        available = true;
      } else {
        available = false;
      }
    }

    return {
      handle: data.handle,
      title: data.title || '',
      price: price,
      compare_at_price: comparePrice,
      image: image,
      url: data.url || ('/products/' + data.handle),
      vendor: data.vendor || '',
      available: available
    };
  }

  /**
   * Hidrata un único producto.
   * Devuelve:
   *   - Objeto producto si OK
   *   - FETCH_404 si el producto no existe (eliminar del cache)
   *   - FETCH_ERROR para otros errores (mantener cache como fallback)
   */
  async function hydrateProduct(handle) {
    if (!handle) return FETCH_ERROR;

    try {
      const response = await fetch(buildProductUrl(handle), {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });

      if (response.status === 404) {
        console.info('[SPAACIO Hydrator] Producto eliminado (404):', handle);
        return FETCH_404;
      }

      if (!response.ok) {
        console.warn('[SPAACIO Hydrator] HTTP error', response.status, 'para', handle);
        return FETCH_ERROR;
      }

      const data = await response.json();
      const normalized = normalizeProduct(data);

      // Si por alguna razón los datos llegan vacíos/inválidos, tratar como 404
      if (!normalized) return FETCH_404;

      return normalized;
    } catch (e) {
      // Error de red, CORS, timeout, etc. — NO es 404
      console.warn('[SPAACIO Hydrator] Network error para', handle, e);
      return FETCH_ERROR;
    }
  }

  /**
   * Limpia handles inválidos del localStorage.
   * Se llama cuando detectamos 404s para que no vuelvan a aparecer.
   */
  function cleanInvalidHandlesFromStorage(invalidHandles) {
    if (!invalidHandles || !invalidHandles.length) return;

    try {
      const STORAGE_KEY = (window.SpaacioRecentlyViewed && window.SpaacioRecentlyViewed.STORAGE_KEY)
        || 'spaacio_recently_viewed';
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const products = JSON.parse(raw);
      if (!Array.isArray(products)) return;

      const cleaned = products.filter(p => p && p.handle && invalidHandles.indexOf(p.handle) === -1);

      if (cleaned.length !== products.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
        console.info('[SPAACIO Hydrator] Limpiados', products.length - cleaned.length, 'handle(s) inválido(s) del cache');
      }
    } catch (e) {
      console.warn('[SPAACIO Hydrator] Error limpiando cache:', e);
    }
  }

  /**
   * Hidrata múltiples productos en paralelo (lista simple de handles).
   */
  async function hydrateProducts(handles) {
    if (!Array.isArray(handles) || handles.length === 0) return [];

    const cleanHandles = [...new Set(handles.filter(h => typeof h === 'string' && h.trim()))];

    const results = await Promise.allSettled(
      cleanHandles.map(h => hydrateProduct(h))
    );

    const products = [];
    const invalidHandles = [];

    results.forEach((result, index) => {
      const handle = cleanHandles[index];
      if (result.status === 'fulfilled') {
        if (result.value === FETCH_404) {
          invalidHandles.push(handle);
        } else if (result.value !== FETCH_ERROR && result.value) {
          products.push(result.value);
        }
      }
    });

    // Auto-limpiar productos eliminados del cache
    if (invalidHandles.length > 0) {
      cleanInvalidHandlesFromStorage(invalidHandles);
    }

    return products;
  }

  /**
   * Hidrata desde una lista cacheada (formato del tracker).
   *
   * Lógica:
   *   - Fetch OK → usar datos frescos (sobreescribe cache)
   *   - 404 → EXCLUIR del resultado Y limpiar localStorage
   *   - Error de red → mantener dato cacheado como fallback
   *
   * @param {Array<Object>} cachedProducts
   * @returns {Promise<Array<Object>>}
   */
  async function hydrateFromCache(cachedProducts) {
    if (!Array.isArray(cachedProducts) || cachedProducts.length === 0) return [];

    const handles = cachedProducts.map(p => p && p.handle).filter(Boolean);

    const results = await Promise.allSettled(
      handles.map(h => hydrateProduct(h))
    );

    const finalProducts = [];
    const invalidHandles = [];

    cachedProducts.forEach((cached, index) => {
      const result = results[index];

      if (!result || result.status !== 'fulfilled') {
        // Promise rejected (raro con allSettled, pero por si acaso)
        // Lo tratamos como error de red → mantener cache
        finalProducts.push(Object.assign({}, cached, { _stale: true }));
        return;
      }

      const value = result.value;

      if (value === FETCH_404) {
        // Producto eliminado: NO incluir en el resultado y marcar para limpiar storage
        invalidHandles.push(cached.handle);
        return;
      }

      if (value === FETCH_ERROR) {
        // Error de red: mantener cache como fallback
        finalProducts.push(Object.assign({}, cached, { _stale: true }));
        return;
      }

      // Datos frescos OK
      if (value && value.handle) {
        finalProducts.push(value);
      }
    });

    // Auto-limpiar productos eliminados del localStorage
    if (invalidHandles.length > 0) {
      cleanInvalidHandlesFromStorage(invalidHandles);
    }

    return finalProducts;
  }

  // API pública
  window.SpaacioProductHydrator = {
    hydrate: hydrateProduct,
    hydrateMany: hydrateProducts,
    hydrateFromCache: hydrateFromCache,
    // Constantes expuestas para debug
    _FETCH_404: FETCH_404,
    _FETCH_ERROR: FETCH_ERROR
  };
})();